/**
 * 会话列表里的「申请会话」两条（21ag；旧版 `chat_list_screen.dart` 的
 * `_applyConversationChat`）。
 *
 * 旧版在会话列表顶部注入两条**虚拟系统会话**：`好友申请` / `群通知`，未读数=
 * 待处理申请数，点进去就是申请处理页。新版此前只在通讯录页画了红点，消息列表
 * 里什么都没有（人工报「加群申请和好友申请……消息列表也要展示出来」）。
 *
 * 口径：
 * - **有才出**（旧版 shouldShowApplyConversation 同口径的简化版：旧版还有
 *   「手动隐藏过就不再出」的本地标记，新版没有隐藏入口，不做）；
 * - 数据源是 contacts.points（friend/point 的 F/G），与通讯录页红点、导航红点
 *   **同一份**——9/24 信令本地 +1、处理完 -1，全程不额外发请求；
 * - 它们不是真会话：私聊/群组两个筛选档里不出现；搜索时按旧版 searchText
 *   （`好友申请 新的朋友 系统通知` / `群通知 入群申请 系统通知`）匹配。
 */
import type { ContactPoints } from '~/chat/contactsModel';

export type ApplyEntryId = 'friend' | 'group';

export interface ApplyEntryView {
  readonly id: ApplyEntryId;
  readonly title: string;
  readonly preview: string;
  /** 待处理条数（>0 才会出现在结果里），角标文案 99+ 封顶由渲染层统一处理 */
  readonly count: number;
}

const ENTRIES: ReadonlyArray<{
  readonly id: ApplyEntryId;
  readonly title: string;
  readonly preview: string;
  /** 旧版 searchText 逐字 */
  readonly search: string;
}> = [
  { id: 'friend', title: '好友申请', preview: '查看好友申请', search: '好友申请 新的朋友 系统通知' },
  { id: 'group', title: '群通知', preview: '查看入群申请', search: '群通知 入群申请 系统通知' },
];

/**
 * 当前该显示哪几条。`filter` 是会话列表的筛选档、`query` 是已归一化的搜索串
 * （conversationFilterModel 的口径：已 trim + 转小写）。
 */
export function applyEntryViews(
  points: ContactPoints | null,
  filter: string,
  query: string,
): readonly ApplyEntryView[] {
  if (points === null) return EMPTY;
  // 私聊/群组档只放真会话；全部/未读档里申请条带着未读数，正是该出现的地方
  if (filter !== 'all' && filter !== 'unread') return EMPTY;
  const out: ApplyEntryView[] = [];
  for (const e of ENTRIES) {
    const count = e.id === 'friend' ? points.friendApplies : points.groupApplies;
    if (count <= 0) continue;
    if (query !== '' && !e.search.includes(query)) continue;
    out.push({ id: e.id, title: e.title, preview: e.preview, count });
  }
  return out.length === 0 ? EMPTY : out;
}

const EMPTY: readonly ApplyEntryView[] = [];

/** 导航「通讯录」项的红点数（两档申请之和；0=不画） */
export function applyBadgeCount(points: ContactPoints | null): number {
  if (points === null) return 0;
  return points.friendApplies + points.groupApplies;
}
